import { createUnionSchema } from '@utils/zodHelper'
import { z } from 'zod'
import {
  createListQuerySchema,
  ORDER_DIRECTIONS,
  paginationSchema,
  timestampsSchema,
} from './api'

export const videoTypes = {
  h264: 'h264',
} as const

const videoTypeSchema = z.nativeEnum(videoTypes)
export type VideoType = z.infer<typeof videoTypeSchema>

export const targetOptions = {
  constantBitrate: 'constantBitrate',
  cappedBitrate: 'cappedBitrate',
  constantQuality: 'constantQuality',
} as const

const targetSchema = z.nativeEnum(targetOptions)
export type Target = z.infer<typeof targetSchema>

//0.5882 is the default values in x264enc (21)
const qualitySchema = z.number({ coerce: true }).min(0.2).max(0.8)

const bitrateSchema = z.number({ coerce: true }).int().min(48).max(25000)
export type Bitrate = z.infer<typeof bitrateSchema>

const lowLatencySchema = z.boolean()
export type LowLatency = z.infer<typeof lowLatencySchema>

export const levelOptions = [
  'l1',
  'l1B',
  'l11',
  'l12',
  'l13',
  'l2',
  'l21',
  'l22',
  'l3',
  'l31',
  'l32',
  'l4',
  'l41',
  'l42',
  'l5',
  'l51',
] as const

const level = z.enum(levelOptions)
export type Level = z.infer<typeof level>

export const profileOptions = [
  'constrainedBaseline',
  'baseline',
  'main',
  'high',
] as const

const profileSchema = z.enum(profileOptions)
export type Profile = z.infer<typeof profileSchema>

const maxKeyframeIntervalSchema = z
  .number({ coerce: true })
  .int()
  .min(1)
  .max(300)
export type MaxKeyframeInterval = z.infer<typeof maxKeyframeIntervalSchema>

const widthSchema = z.number({ coerce: true }).int().min(16).max(1920)
const heightSchema = z.number({ coerce: true }).int().min(16).max(1080)

export const dimensionsSchema = z.object({
  width: widthSchema,
  height: heightSchema,
})
export type Dimensions = z.infer<typeof dimensionsSchema>

const h264OptionsSchema = z.object({
  target: targetSchema.nullable().transform((v) => v || null),
  quality: qualitySchema.nullable().transform((v) => v ?? null),
  bitrate: bitrateSchema.nullable().transform((v) => v || null),
  lowLatency: lowLatencySchema.nullable().transform((v) => v ?? null),
  level: level.nullable().transform((v) => v || null),
  profile: profileSchema.nullable().transform((v) => v || null),
  maxKeyframeInterval: maxKeyframeIntervalSchema
    .nullable()
    .transform((v) => v || null),
})
export type H264Options = z.infer<typeof h264OptionsSchema>

const fpsSchema = z.number({ coerce: true }).int().min(1).max(60)
export type Fps = z.infer<typeof fpsSchema>

export type Quality = z.infer<typeof qualitySchema>

export const videoEncodingPresetSchema = z
  .object({
    type: videoTypeSchema.default('h264'),
    dimensions: dimensionsSchema.nullable(),
    fps: fpsSchema.nullable(),
    h264Options: h264OptionsSchema,
  })
  .transform((data) => {
    if (
      data.dimensions &&
      (!data.dimensions.width || !data.dimensions.height)
    ) {
      return { ...data, dimensions: null }
    }
    return data
  })

export type VideoEncodingPreset = z.infer<typeof videoEncodingPresetSchema>

export const audioTypes = {
  aac: 'aac',
  mp2: 'mp2',
  opus: 'opus',
} as const

const audioTypeSchema = z.nativeEnum(audioTypes)
export type AudioType = z.infer<typeof audioTypeSchema>

const audioBitrateSchema = z.number({ coerce: true }).int().min(16).max(512)
export type AudioBitrate = z.infer<typeof audioBitrateSchema>

export const sampleRateOptions = [22050, 32000, 44100, 48000] as const

const sampleRateSchema = z.coerce
  .number()
  .pipe(createUnionSchema(sampleRateOptions))
export type SampleRate = z.infer<typeof sampleRateSchema>

const muteSchema = z.boolean()
export type Mute = z.infer<typeof muteSchema>

export const audioEncodingPresetSchema = z.object({
  type: audioTypeSchema,
  bitrate: audioBitrateSchema.nullable().transform((v) => v ?? null),
  sampleRate: sampleRateSchema.nullable().transform((v) => v ?? null),
  mute: muteSchema.nullable().transform((v) => v ?? null),
})
export type AudioEncodingPreset = z.infer<typeof audioEncodingPresetSchema>

export const encodingPresetIdSchema = z.string().uuid()
export type EncodingPresetId = z.infer<typeof encodingPresetIdSchema>

export type WithEncodingPresetId<T> = T & { encodingPresetId: EncodingPresetId }

export const encodingPresetSchema = z.object({
  encodingPresetId: encodingPresetIdSchema,
  name: z.string().min(1),
  description: z.string().nullish(),
  encodingProfile: z.object({
    audio: audioEncodingPresetSchema,
    video: videoEncodingPresetSchema,
  }),
})
export type EncodingPreset = z.infer<typeof encodingPresetSchema>

export const createEncodingPresetSchema = encodingPresetSchema
  .omit({
    encodingPresetId: true,
  })
  .extend({
    encodingProfile: z.object({
      audio: audioEncodingPresetSchema.default({
        type: 'aac',
        bitrate: null,
        sampleRate: null,
        mute: null,
      }),
      video: videoEncodingPresetSchema.default({
        type: 'h264',
        dimensions: null,
        fps: null,
        h264Options: {
          target: null,
          quality: null,
          bitrate: null,
          lowLatency: null,
          level: null,
          profile: null,
          maxKeyframeInterval: null,
        },
      }),
    }),
  })

export type CreateEncodingPreset = z.infer<typeof createEncodingPresetSchema>

export const updateEncodingPresetSchema = encodingPresetSchema
  .omit({ encodingPresetId: true })
  .partial()
export type UpdateEncodingPreset = z.infer<typeof updateEncodingPresetSchema>

export const encodingPresetDetailsSchema =
  encodingPresetSchema.and(timestampsSchema)
export type EncodingPresetDetails = z.infer<typeof encodingPresetDetailsSchema>

export const listEncodingPresetResponseSchema = z
  .object({
    items: z.array(encodingPresetDetailsSchema),
  })
  .and(paginationSchema)

export type ListEncodingPresetResponse = z.infer<
  typeof listEncodingPresetResponseSchema
>

export const DEFAULT_ORDER = {
  field: 'name',
  direction: ORDER_DIRECTIONS.descending,
}

export const listEncodingPresetsRequestSchema = createListQuerySchema(
  ['name', 'createdAt'],
  {
    orderBy: [DEFAULT_ORDER],
  }
)
export type ListEncodingPresetsRequest = z.infer<
  typeof listEncodingPresetsRequestSchema
>
